import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projets as staticProjets } from '../data/siteData';
import { fp40iaImages, adaImages } from '../assets';
import { getPublicProjets } from '../services/contentApi';
import useCmsPage from '../hooks/useCmsPage';
import useSeoMeta from '../hooks/useSeoMeta';
import './Projets.css';

const coverImages = {
  fp40ia: fp40iaImages.img002,
  ada: adaImages[0],
};

// Map statut DB → label affiché + clé coverImage
const STATUS_LABEL = { en_cours: 'En cours', termine: 'Complété' };
const COVER_KEY    = { 'forprode-40-ia': 'fp40ia', 'atos-digital-academy': 'ada' };

function mergeApiData(apiList) {
  return staticProjets.map(sp => {
    const live = apiList.find(a => a.id === sp.id);
    if (!live) return sp;
    return {
      ...sp,
      title:           live.title           || sp.title,
      description:     live.description     || sp.description,
      longDescription: live.long_description || sp.longDescription,
      partner:         live.partner         || sp.partner,
      year:            live.year            || sp.year,
      participants:    live.participants     ?? sp.participants,
      duration:        live.duration        || sp.duration,
      location:        live.location        || sp.location,
      status:          STATUS_LABEL[live.status] || sp.status,
      formations:      Array.isArray(live.tags) && live.tags.length ? live.tags : sp.formations,
      coverImage:      COVER_KEY[sp.id] || sp.coverImage,
      imageUrl:        live.image_url       || null,
      lienExterne:     live.lien_externe    || sp.lienExterne || null,
    };
  });
}

export default function Projets() {
  const { sections, page } = useCmsPage('projets');
  useSeoMeta(page, { title: 'Projets — Tech Talent Accelerator', desc: "Découvrez les projets d'insertion et de formation tech portés par TTA." });
  const getSec = type => sections.find(s => s.type === type)?.content || {};
  const cmsHero = getSec('hero');
  const [filter, setFilter]   = useState('all');
  const [projets, setProjets] = useState(staticProjets);

  useEffect(() => {
    getPublicProjets().then(data => {
      if (data && data.length) setProjets(mergeApiData(data));
    });
  }, []);

  const filtered = projets.filter(p => {
    if (filter === 'encours')   return p.status === 'En cours';
    if (filter === 'completes') return p.status.startsWith('Complét');
    return true;
  });
  return (
    <main className="projets-page">
      <section className="projets-hero">
        <div className="projets-hero__bg" />
        <div className="page-hero-right__orb" data-parallax="0.55" />
        <div className="page-hero-right__orb2" data-parallax="-0.20" />
        <div className="page-hero-layout">
          <div className="page-hero-left">
            <div className="section-eyebrow">{cmsHero.eyebrow || 'Nos réalisations'}</div>
            <h1 className="section-title" style={{ fontSize: '44px', maxWidth: '520px' }}>
              {cmsHero.title || 'Des programmes'}<br /><span className="grad-text">{cmsHero.title_grad || 'à impact réel'}</span>
            </h1>
            <p className="section-subtitle" style={{ maxWidth: '460px' }}>
              {cmsHero.subtitle || "De la conception au déploiement, chaque projet TTA forme des talents et change des trajectoires professionnelles."}
            </p>
            <div className="hero-filter-bar">
              {[
                { key: 'all',       label: 'Tous les projets' },
                { key: 'encours',   label: 'En cours' },
                { key: 'completes', label: 'Complétés' },
              ].map(f => (
                <button key={f.key} className={`hero-filter-btn ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
              ))}
            </div>
          </div>
          <div className="page-hero-right">
            <div className="phm-grid">
              {[
                { num: '20+', label: 'Projets réalisés avec succès', bar: '80%', delay: '0s', barDelay: '0.2s',
                  icon: <><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></> },
                { num: '500+', label: 'Talents formés à travers les projets', bar: '85%', delay: '1.5s', barDelay: '0.4s',
                  icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></> },
                { num: '6', label: "Secteurs d'activité couverts", bar: '65%', delay: '3s', barDelay: '0.6s',
                  icon: <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></> },
                { num: '8+', label: 'Villes et pays impactés', bar: '70%', delay: '4.5s', barDelay: '0.8s',
                  icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></> },
              ].map((m, i) => (
                <div key={i} className="phm-card" style={{ animationDelay: m.delay }}>
                  <div className="phm-card__icon">
                    <svg viewBox="0 0 24 24">{m.icon}</svg>
                  </div>
                  <div className="phm-card__num">{m.num}</div>
                  <div className="phm-card__label">{m.label}</div>
                  <div className="phm-card__bar">
                    <div className="phm-card__bar-fill" style={{ width: m.bar, animationDelay: m.barDelay }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="projets-list">
          {filtered.map(p => (
            <Link to={`/projets/${p.slug}`} key={p.id} className="projet-detail-card">
              <div className="projet-detail-card__inner">
                {(p.imageUrl || coverImages[p.coverImage]) && (
                  <div className="projet-detail-card__img-wrap">
                    <img src={p.imageUrl || coverImages[p.coverImage]} alt={p.title} className="projet-detail-card__img" />
                    <span className={`projet-detail-card__img-badge ${p.status === 'Complété' ? 'done' : 'active'}`}>
                      {p.status}
                    </span>
                  </div>
                )}
                <div className="projet-detail-card__left">
                  <div className="projet-detail-card__year">{p.year}</div>
                  <h2 className="projet-detail-card__title">{p.title}</h2>
                  <p className="projet-detail-card__desc">{p.longDescription}</p>
                  <div className="projet-detail-card__tags">
                    {p.formations.map(f => (
                      <span key={f} className="projet-detail-card__tag">{f}</span>
                    ))}
                  </div>
                </div>
                <div className="projet-detail-card__right">
                  <div className="projet-detail-card__stat">
                    <span className="grad-text" style={{ fontFamily: "'Sora',sans-serif", fontSize: '36px', fontWeight: 800 }}>{p.participants}</span>
                    <span style={{ fontSize: '12px', color: '#777' }}>participants formés</span>
                  </div>
                  <div className="projet-detail-card__meta">
                    <div className="projet-detail-card__meta-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                      {p.duration}
                    </div>
                    <div className="projet-detail-card__meta-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {p.location}
                    </div>
                    <div className="projet-detail-card__meta-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                      {p.partner}
                    </div>
                  </div>
                  <span className="projet-detail-card__cta">Voir le projet →</span>
                  {p.lienExterne && (
                    <a href={p.lienExterne} target="_blank" rel="noopener noreferrer" className="projet-detail-card__ext-link" onClick={e => e.stopPropagation()}>
                      Site du projet ↗
                    </a>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="section section-alt" style={{ textAlign: 'center' }}>
        <div className="section-eyebrow">Vous avez un projet ?</div>
        <h2 className="section-title">Co-construisons votre programme</h2>
        <p className="section-subtitle" style={{ margin: '0 auto 28px' }}>
          Vous êtes une institution, un bailleur ou une entreprise ? Contactez-nous pour concevoir ensemble votre programme de formation sur-mesure.
        </p>
        <Link to="/contact" className="btn-primary" style={{ fontSize: '14px', padding: '13px 28px' }}>
          Lancer un projet avec TTA →
        </Link>
      </section>
    </main>
  );
}
